import {
  Component,
  Input,
  OnDestroy,
  AfterViewInit,
  effect,
  signal,
  computed,
  inject,
} from '@angular/core';
import { AssetsService, logError, LP_UI_CONFIG } from '@lp-ui-lib';
import { I18nService } from '@lp-ui-lib';
import { UiService } from '@lp-ui-lib';
import { SpinnerComponent } from '@lp-ui-lib';
import { Marker } from '@lp-ui-lib';

const M = window['L'];

@Component({
  selector: 'app-asset-overview-map',
  imports: [SpinnerComponent],
  templateUrl: './asset-overview-map.html',
  styleUrls: ['./asset-overview-map.scss'],
})
export class AssetOverviewMapComponent implements AfterViewInit, OnDestroy {
  Map: any = null;
  tileLayer: any = null;

  // Signals
  center = signal<[number, number]>([51.175545, 10.2180081]);
  zoomLevel = signal<number>(13);
  satelliteView = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  sensor = signal<any>(null);
  markers = signal<any>(new Marker());
  addedMarker = signal<any>(null);

  private readonly cfg = inject(LP_UI_CONFIG);
  public i18n = inject(I18nService);
  private ui = inject(UiService);
  private assetsService = inject(AssetsService);

  // Inputs as signals
  @Input() set SensorPosition(position: { lat: number; lng: number }) {
    this.center.set([position.lat, position.lng]);
  }

  @Input() set Sensor(value: any) {
    this.sensor.set(value);
  }

  @Input() set Loading(value: boolean) {
    this.isLoading.set(value);
  }

  readonly isMobile = computed(() => this.ui.IsMobile());
  readonly isTablet = computed(() => this.ui.IsTablet());
  readonly isDesktop = computed(() => this.ui.IsDesktop());

  constructor() {
    // Effect to update marker when sensor or center changes
    effect(() => {
      this.updateMarker();
      this.addMarkers();
    });

    // Effect to update map view when center or zoom changes
    effect(() => {
      if (this.Map && this.center()) {
        this.Map.setView(this.center(), this.zoomLevel());
      }
    });
  }

  ngAfterViewInit() {
    this.initMap();
  }

  ngOnDestroy() {
    if (this.Map) {
      this.Map.off();
      this.Map.remove();
    }
  }

  initMap() {
    if (this.Map) {
      this.Map.off();
      this.Map.remove();
    }

    M.Map.addInitHook('addHandler', 'scrollWheelZoom', M.Map.prototype.scrollWheelZoom);
    //delete M.Icon.Default.prototype._getIconUrl;

    this.Map = M.map('AssetOverviewMap', {
      zoomControl: false,
      boxZoom: true,
      zoomAnimation: true,
    });

    this.addTiles();

    setTimeout(() => {
      try {
        this.Map.invalidateSize();
        if (this.center()) {
          this.Map.setView(this.center(), this.zoomLevel());
        }
      } catch (e) {
        logError(e);
      }
      this.addMarkers();
    }, 500);

    this.Map.on('zoom', (event: any) => {
      this.zoomLevel.set(event.target._zoom);
    });
  }

  addTiles() {
    if (this.tileLayer) {
      this.Map.removeLayer(this.tileLayer);
    }

    this.assetsService.getMapBoxKeys().then((keys) => {
      const baseUrl = this.satelliteView() ? keys.satelliteView : keys.mapView;
      const tileUrl = baseUrl.replace('{accessToken}', keys.geoCodingToken);

      this.tileLayer = M.tileLayer(tileUrl, {
        maxZoom: 18,
        tileSize: 512,
        zoomOffset: -1,
        attribution: 'Map data © OpenStreetMap contributors, Imagery © Mapbox',
      });

      this.tileLayer.addTo(this.Map);
      setTimeout(() => this.Map.invalidateSize(), 0);
    });
  }

  updateMarker() {
    const sensor = this.sensor();
    const center = this.center();

    const html =
      sensor?.Inactive && !sensor?.Critical && !sensor?.Warning
        ? `<div class="sub-marker body-bold">            
          <div class="asset-icon inactive">
            <img class="fillwhite" src="assets/icons/wifi.svg">
          </div>
        </div>`
        : sensor?.Critical && !sensor?.Inactive && !sensor?.Warning
          ? `<div class="sub-marker body-bold">            
          <div class="asset-icon critical">
            <img class="fillwhite" src="assets/icons/wifi.svg">
          </div>
        </div>`
          : sensor?.Warning && !sensor?.Inactive && !sensor?.Critical
            ? `<div class="sub-marker body-bold">            
          <div class="asset-icon warning">
            <img src="assets/icons/wifi.svg">
          </div>
        </div>`
            : `<div class="sub-marker body-bold">            
          <div class="asset-icon working">
            <img src="assets/icons/wifi.svg">
          </div>
        </div>`;

    const marker = M.marker(center, {
      icon: M.divIcon({
        className: 'sensor-marker',
        html,
        iconSize: [56, 68],
        iconAnchor: [28, 68],
      }),
    });

    this.addedMarker.set(marker);
  }

  addMarkers() {
    if (this.Map) {
      const group = M.layerGroup();
      const marker = this.addedMarker();
      if (marker) {
        marker.setLatLng({ lat: this.center()[0], lng: this.center()[1] });
        group.addLayer(marker);
      }

      if (this.markers()) {
        this.Map.removeLayer(this.markers());
      }

      this.markers.set(group);
      this.Map.addLayer(group);
    }
  }
}
